import { T } from 'gt-react';
import { cn } from '@/lib/utils';
import { EmailData } from './email-viewer-types';
import { Card, CardContent } from '@/components/ui/card';
import { processEmailHtml } from './email-html';

const renderedEmailOverrides = cn(
  '[&_img]:max-w-full',
  '[&_a]:text-blue-600',
  '[&_p]:mb-4 [&_p]:leading-6 [&_p]:text-sm [&_p]:text-gray-900',
  '[&_h1]:mb-2 [&_h1]:text-gray-900',
  '[&_h2]:mb-2 [&_h2]:text-gray-900',
  '[&_h3]:mb-2 [&_h3]:text-gray-900',
  '[&_h4]:mb-2 [&_h4]:text-gray-900',
  '[&_h5]:mb-2 [&_h5]:text-gray-900',
  '[&_h6]:mb-2 [&_h6]:text-gray-900',
  '[&_ul]:pl-6 [&_ul]:mb-4',
  '[&_ol]:pl-6 [&_ol]:mb-4',
  '[&_blockquote]:border-l-4 [&_blockquote]:border-gray-300 [&_blockquote]:pl-4 [&_blockquote]:ml-0 [&_blockquote]:mb-4 [&_blockquote]:italic',
  '[&_pre]:bg-gray-50 [&_pre]:p-4 [&_pre]:rounded [&_pre]:overflow-x-auto [&_pre]:mb-4',
  '[&_code]:bg-gray-50 [&_code]:px-2 [&_code]:py-1 [&_code]:rounded [&_code]:text-sm',
);

function normalizeContentId(contentId: string): string {
  let normalized = contentId.trim().replace(/^<|>$/g, '');
  const atIndex = normalized.indexOf('@');
  if (atIndex !== -1) {
    normalized = normalized.substring(0, atIndex);
  }
  return normalized;
}

function addCidMapping(
  cidMap: Map<string, string>,
  key?: string,
  url?: string,
) {
  if (!key || !url) return;
  const normalized = normalizeContentId(key);
  if (!normalized) return;
  cidMap.set(normalized, url);
}

function buildCidMap(emailData: EmailData): Map<string, string> {
  const cidMap = new Map<string, string>();

  for (const attachment of emailData.attachments ?? []) {
    addCidMapping(cidMap, attachment.contentId ?? undefined, attachment.s3Url);
    addCidMapping(cidMap, attachment.id, attachment.s3Url);
    addCidMapping(cidMap, attachment.fileName, attachment.s3Url);
  }

  for (const association of emailData.documentAssociations ?? []) {
    addCidMapping(
      cidMap,
      association.document.contentId ?? undefined,
      association.document.url,
    );
    addCidMapping(cidMap, association.document.id, association.document.url);
    addCidMapping(
      cidMap,
      association.document.originalFileName || association.document.name,
      association.document.url,
    );
  }

  return cidMap;
}

export function EmailBody({ emailData }: { emailData: EmailData }) {
  const htmlContent = emailData.htmlContent;
  const textContent = emailData.textContent;

  // Check if we have any content at all
  const hasHtmlContent =
    htmlContent &&
    htmlContent
      .replace(/<[^>]*>/g, '') // Remove HTML tags
      .replace(/&nbsp;/g, ' ') // Replace &nbsp; with space
      .trim().length > 0;

  const hasTextContent = textContent && textContent.trim().length > 0;

  const hasAnyContent = hasHtmlContent || hasTextContent;

  if (!hasAnyContent) {
    return (
      <Card>
        <CardContent className="flex justify-center items-center">
          <span className="text-xs text-gray-400 italic tracking-wide opacity-20">
            <T context="empty state when an email has no HTML or text body">
              No email body content
            </T>
          </span>
        </CardContent>
      </Card>
    );
  }

  // Render HTML content if available
  if (hasHtmlContent) {
    const cidMap = buildCidMap(emailData);
    const sanitizedContent = processEmailHtml({
      html: htmlContent,
      cidMap,
    });

    return (
      <Card>
        <CardContent
          className={cn(
            'bg-white prose prose-sm max-w-none',
            renderedEmailOverrides,
          )}
        >
          <div
            dangerouslySetInnerHTML={{
              __html: sanitizedContent,
            }}
          />
        </CardContent>
      </Card>
    );
  }

  // Fallback to text content with proper line break handling
  return (
    <Card>
      <CardContent className="bg-white">
        <div className="text-sm text-gray-900 leading-6 whitespace-pre-wrap break-words">
          {textContent}
        </div>
      </CardContent>
    </Card>
  );
}
